import { Box, Button, Flex, Text } from "@radix-ui/themes";
import * as Icons from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { ExternalLink } from "lucide-react";
import { useAuth } from "@/providers/AuthProvider";
import { DashboardWidget } from "@/types/breeze/Dashboard";

interface Props {
  widget: DashboardWidget;
}

export function QuickActionWidget({ widget }: Props) {
  const { safeNavigate } = useAuth();
  const config = parseConfig(widget.config_json);

  const Icon = config?.icon
    ? ((Icons as unknown as Record<string, LucideIcon>)[config.icon] ?? Icons.Zap)
    : Icons.Zap;

  const isExternal = !!config?.url;

  const handleClick = () => {
    if (config?.url) {
      const normalized = /^[a-z][a-z0-9+.-]*:\/\//i.test(config.url)
        ? config.url
        : `https://${config.url}`;
      const target = config.target === "_self" ? "_self" : "_blank";
      if (target === "_blank") {
        window.open(normalized, "_blank", "noopener,noreferrer");
      } else {
        window.location.href = normalized;
      }
    } else if (config?.route) {
      safeNavigate(config.route);
    }
  };

  return (
    <Box p="3" style={{ height: "100%" }}>
      <Flex direction="column" gap="2" style={{ height: "100%" }}>
        <Flex align="center" gap="2">
          <Icon size={16} />
          <Text size="2" weight="medium">{widget.title}</Text>
        </Flex>
        {config?.description && (
          <Text size="1" color="gray">{config.description}</Text>
        )}
        <Button
          size="2"
          onClick={handleClick}
          disabled={!config?.route && !config?.url}
        >
          {config?.label ?? "Open"}
          {isExternal && <ExternalLink size={14} />}
        </Button>
      </Flex>
    </Box>
  );
}

function parseConfig(configJson: string | null): {
  label?: string;
  icon?: string;
  route?: string;
  url?: string;
  target?: "_blank" | "_self";
  description?: string;
} | null {
  if (!configJson) return null;
  try {
    return JSON.parse(configJson);
  } catch {
    return null;
  }
}
